"use client";

import { createContext, useContext, useMemo } from "react";

export type WirePaymentMethodConfig = {
  id: "wire";
  name: string;
  enabled: boolean;
  discountActive: boolean;
  discountPercent: number;
  instructions: string[];
  instructionsText: string;
};

export type MercadoPagoPaymentMethodConfig = {
  id: "mercadopago";
  name: string;
  enabled: boolean;
  configured: boolean;
  publicKey?: string | null;
  integratorId?: string | null;
  testMode?: boolean;
};

export type GetnetSantanderPaymentMethodConfig = {
  id: "getnetSantander";
  name: string;
  enabled: boolean;
  configured: boolean;
  testMode?: boolean;
  defaults?: {
    documentType?: string;
    currency?: string;
  };
  capabilities?: {
    documentTypes?: Array<{ code: string; label: string }>;
  };
};

export type SantanderCheckoutPaymentMethodConfig = {
  id: "santanderCheckout";
  name: string;
  enabled: boolean;
  configured: boolean;
  testMode?: boolean;
  defaults?: {
    documentType?: string;
    currency?: string;
    locale?: string;
  };
  capabilities?: {
    documentTypes?: Array<{ code: string; label: string }>;
  };
};

export type ProductInstallmentsConfig = {
  enabled: boolean;
  installments: number;
  message: string;
};

export type PaymentMethodConfig =
  | WirePaymentMethodConfig
  | MercadoPagoPaymentMethodConfig
  | GetnetSantanderPaymentMethodConfig
  | SantanderCheckoutPaymentMethodConfig;

export type PaymentMethodsContextValue = {
  methods: PaymentMethodConfig[];
  wire?: WirePaymentMethodConfig;
  mercadoPago?: MercadoPagoPaymentMethodConfig;
  getnetSantander?: GetnetSantanderPaymentMethodConfig;
  santanderCheckout?: SantanderCheckoutPaymentMethodConfig;
  productInstallments?: ProductInstallmentsConfig;
};

const defaultValue: PaymentMethodsContextValue = { methods: [] };

const PaymentMethodsContext = createContext<PaymentMethodsContextValue>(defaultValue);

export function PaymentMethodsProvider({
  initialValue,
  children,
}: {
  initialValue: PaymentMethodsContextValue;
  children: React.ReactNode;
}) {
  const memoized = useMemo(() => {
    let next = initialValue;
    if (!next.wire) {
      const wireMethod = next.methods.find((method) => method.id === "wire") as
        | WirePaymentMethodConfig
        | undefined;
      if (wireMethod) {
        next = { ...next, wire: wireMethod };
      }
    }
    if (!next.mercadoPago) {
      const mpMethod = next.methods.find((method) => method.id === "mercadopago") as
        | MercadoPagoPaymentMethodConfig
        | undefined;
      if (mpMethod) {
        next = { ...next, mercadoPago: mpMethod };
      }
    }
    if (!next.getnetSantander) {
      const getnetMethod = next.methods.find(
        (method) => method.id === "getnetSantander"
      ) as GetnetSantanderPaymentMethodConfig | undefined;
      if (getnetMethod) {
        next = { ...next, getnetSantander: getnetMethod };
      }
    }
    if (!next.santanderCheckout) {
      const santanderCheckoutMethod = next.methods.find(
        (method) => method.id === "santanderCheckout"
      ) as SantanderCheckoutPaymentMethodConfig | undefined;
      if (santanderCheckoutMethod) {
        next = { ...next, santanderCheckout: santanderCheckoutMethod };
      }
    }
    return next;
  }, [initialValue]);

  return <PaymentMethodsContext.Provider value={memoized}>{children}</PaymentMethodsContext.Provider>;
}

export function usePaymentMethods() {
  return useContext(PaymentMethodsContext);
}
